/**
 * Maps chat changes to their location in the app (tab, section, DOM field)
 * and provides the post-apply "flash" highlight that confirms where a
 * change landed. Shared by the change confirmation modal and ChatPanel.
 */

import type { ChatChange } from "../api/client";
import { prettyFieldLabel } from "./labels";

export interface ChangeLocation {
  route: string;
  tabName: string;
  section: string;
}

/** Map a chat change field to the tab route and human-readable location. */
export function fieldToTab(change: ChatChange): ChangeLocation {
  const field = change.field_name || "";
  const target = change.target || "scenario";

  // Meta-actions route to the tab where their effect is visible
  if (field === "_add_tenant" || field === "_remove_tenant")
    return { route: "rent-roll", tabName: "Rent Roll", section: "Tenant Roster" };
  if (field === "_create_profile" || field === "_remove_profile" || field === "_assign_profile")
    return { route: "market-leasing", tabName: "Market Leasing", section: "Leasing Profiles" };
  if (field === "_add_other_income" || field === "_remove_other_income" || field === "_edit_other_income")
    return { route: "cash-flow", tabName: "Pro Forma", section: "Other Income" };

  if (field.startsWith("profile."))
    return { route: "market-leasing", tabName: "Market Leasing", section: "Leasing Profiles" };

  if (target === "tenant") {
    if (field === "lease_type" || field.startsWith("cam_structure"))
      return { route: "reimbursements", tabName: "Reimbursements", section: "Tenant Setup" };
    return { route: "rent-roll", tabName: "Rent Roll", section: "Tenant Data" };
  }

  if (field.startsWith("opex.") || field.startsWith("expense_override."))
    return { route: "expenses", tabName: "Expenses", section: "Operating Expenses" };

  if (field.startsWith("rollover_assumptions.") || field.startsWith("rollover_override."))
    return { route: "market-leasing", tabName: "Market Leasing", section: "Rollover Assumptions" };

  if (field.startsWith("debt."))
    return { route: "assumptions", tabName: "Assumptions", section: "Debt Assumptions" };

  const assumptionFields = [
    "vacancy_rate_pct", "credit_loss_pct", "expense_growth_rate_pct",
    "tax_growth_rate_pct", "insurance_growth_rate_pct", "market_rent_growth_pct_annual",
    "capital_reserves_psf", "management_fee_pct", "general_vacancy_pct",
  ];
  if (assumptionFields.some(f => field.startsWith(f)))
    return { route: "assumptions", tabName: "Assumptions", section: "Scenario Assumptions" };

  if (field.startsWith("override."))
    return { route: "assumptions", tabName: "Assumptions", section: "Year-Level Overrides" };

  if (field.startsWith("other_income."))
    return { route: "cash-flow", tabName: "Pro Forma", section: "Other Income" };

  const dealFields = [
    "purchase_price", "exit_cap_rate", "going_in_cap_rate", "discount_rate",
    "hold_period_years", "selling_costs_pct", "total_leasable_area",
    "analysis_start_date",
  ];
  if (dealFields.includes(field))
    return { route: "assumptions", tabName: "Assumptions", section: "Deal Assumptions" };

  return { route: "cash-flow", tabName: "Pro Forma", section: "Cash Flow" };
}

/** Human label for a change's field, e.g. "CAM Expense Cap (Year 3)". */
export function displayField(field: string): string {
  let prefix = "";
  let name = field
    .replace(/^_/, "") // meta-actions: "_add_tenant" → "Add Tenant"
    .replace(/^rollover_assumptions\./, "")
    .replace(/^rollover_override\./, "")
    .replace(/^debt\./, "")
    .replace(/^override\./, "")
    .replace(/^opex\./, "")
    .replace(/^expense_override\./, "")
    .replace(/^other_income\./, "")
    .replace(/^profile\./, "");
  if (name.startsWith("cam_structure.")) {
    prefix = "CAM ";
    name = name.replace(/^cam_structure\./, "");
  }
  const ym = name.match(/^(.+)\.(\d+)$/);
  let suffix = "";
  if (ym) {
    name = ym[1];
    suffix = ` (Year ${ym[2]})`;
  }
  return prefix + prettyFieldLabel(name) + suffix;
}

/** Build data-field-id selectors for locating the changed field on the page.
 *  Returns multiple selectors to try (with and without prefix). */
export function fieldSelectors(change: ChatChange): string[] {
  const field = change.field_name || "";
  const value = change.new_value as Record<string, unknown> | string | null;

  // Meta-actions: anchor to the element representing the affected entity
  if (field === "_add_tenant" || field === "_remove_tenant") {
    const name = typeof value === "string" ? value : String((value as Record<string, unknown>)?.tenant_name ?? "");
    return name ? [`[data-field-id="tenant_name"][data-tenant="${name}"]`] : [];
  }
  if (field === "_assign_profile") {
    const name = typeof value === "object" && value ? String(value.tenant_name ?? "") : "";
    return name ? [`[data-field-id="market_leasing_profile_id"][data-tenant="${name}"]`] : [`[data-field-id="market_leasing_profile_id"]`];
  }
  if (field === "_create_profile" || field === "_remove_profile") {
    return [`[data-field-id="profile_name"]`];
  }

  const tenant = change.tenant_name || "";
  // Strip common prefixes for matching against fieldKey on elements
  const stripped = field
    .replace(/^rollover_assumptions\./, "")
    .replace(/^rollover_override\./, "")
    .replace(/^opex\./, "")
    .replace(/^expense_override\./, "")
    .replace(/^override\./, "");
  const tenantAttr = tenant ? `[data-tenant="${tenant}"]` : "";
  const selectors = [`[data-field-id="${field}"]${tenantAttr}`];
  if (stripped !== field) selectors.push(`[data-field-id="${stripped}"]${tenantAttr}`);
  // profile.<field> / profile.<name>.<field> → match the bare profile field
  if (field.startsWith("profile.")) {
    const last = field.split(".").pop() || "";
    if (last) selectors.push(`[data-field-id="${last}"]`);
  }
  // Rollover field whose profile-editor input uses the profile naming
  if (stripped === "market_rent_growth_pct_annual")
    selectors.push(`[data-field-id="annual_rent_increase_pct"]`);
  // cam_structure.* sub-fields fall back to the tenant's CAM anchor
  if (field.startsWith("cam_structure."))
    selectors.push(`[data-field-id="cam_structure"]${tenantAttr}`);
  return selectors;
}

const FLASH_CLASS = "autoval-change-flash";

/**
 * Flash a green confirmation highlight on every changed field currently (or
 * soon to be) in the DOM. Retries for a few seconds because the page often
 * re-renders right after a projection refresh; the first matching element per
 * change wins. The first match is also scrolled into view.
 */
export function flashChanges(changes: ChatChange[]): void {
  const pending = changes.map(fieldSelectors).filter(s => s.length > 0);
  if (!pending.length) return;

  let attempts = 0;
  let scrolled = false;
  const maxAttempts = 40; // 40 × 100ms = 4s of retries
  const interval = window.setInterval(() => {
    attempts += 1;
    for (let i = pending.length - 1; i >= 0; i--) {
      for (const selector of pending[i]) {
        const el = document.querySelector(selector);
        if (el) {
          // Restart the animation if the element was already flashed
          el.classList.remove(FLASH_CLASS);
          void (el as HTMLElement).offsetWidth;
          el.classList.add(FLASH_CLASS);
          window.setTimeout(() => el.classList.remove(FLASH_CLASS), 2600);
          if (!scrolled) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            scrolled = true;
          }
          pending.splice(i, 1);
          break;
        }
      }
    }
    if (!pending.length || attempts >= maxAttempts) {
      window.clearInterval(interval);
    }
  }, 100);
}
